import apiService from './api';

interface SystemConfig {
  emailDomain: string;
  configSource: string;
  timestamp: string;
}

// Default email domain (fallback if API fails)
const DEFAULT_EMAIL_DOMAIN = 'qixit.com';

// Cached config
let cachedConfig: SystemConfig | null = null;
let configLoadPromise: Promise<SystemConfig> | null = null;

/**
 * Fetches system configuration from the API.
 * The config is cached for the lifetime of the app.
 */
export async function getSystemConfig(): Promise<SystemConfig> {
  // Return cached config if available
  if (cachedConfig) {
    return cachedConfig;
  }
  
  // If a fetch is already in progress, wait for it
  if (configLoadPromise) {
    return configLoadPromise;
  }
  
  // Start fetching
  configLoadPromise = fetchSystemConfig();
  
  try {
    cachedConfig = await configLoadPromise;
    return cachedConfig;
  } finally {
    configLoadPromise = null;
  }
}

async function fetchSystemConfig(): Promise<SystemConfig> {
  try {
    const config = await apiService.get<SystemConfig>('/api/systeminfo/config');
    console.log('📧 System config loaded, emailDomain:', config.emailDomain);
    return config;
  } catch (error) {
    console.warn('⚠️ Failed to load system config, using defaults:', error);
    // Return default config on error
    return {
      emailDomain: DEFAULT_EMAIL_DOMAIN,
      configSource: 'default',
      timestamp: new Date().toISOString()
    };
  }
}

/**
 * Gets the email domain from the system config.
 * This is a convenience function that returns just the domain.
 */
export async function getEmailDomain(): Promise<string> {
  const config = await getSystemConfig();
  return config.emailDomain || DEFAULT_EMAIL_DOMAIN;
}

/**
 * Returns the cached email domain synchronously.
 * Falls back to default if config hasn't been loaded yet.
 * Use this in render methods where async isn't possible.
 */
export function getEmailDomainSync(): string {
  return cachedConfig?.emailDomain || DEFAULT_EMAIL_DOMAIN;
}

/**
 * Constructs a full email address from a username.
 * @param username The username without domain (e.g., "john")
 * @returns Full email address (e.g., "john@qixit.com")
 */
export function formatEmailAddress(username: string): string {
  const domain = getEmailDomainSync();
  // If already has @, return as-is
  if (username.includes('@')) {
    return username;
  }
  return `${username}@${domain}`;
}

/**
 * Extracts username from an email address.
 * @param email Full email address (e.g., "john@qixit.com")
 * @returns Username only (e.g., "john")
 */
export function extractUsername(email: string): string {
  if (!email.includes('@')) {
    return email;
  }
  return email.split('@')[0];
}

/**
 * Pre-loads the system config. Call this early in app initialization.
 */
export async function preloadSystemConfig(): Promise<void> {
  await getSystemConfig();
}

export default {
  getSystemConfig,
  getEmailDomain,
  getEmailDomainSync,
  formatEmailAddress,
  extractUsername,
  preloadSystemConfig
};
